import { publicApi } from "@/lib/api";
import { visitorTrackingEnabled } from "@/config/features";
import { getVisitorId, getSessionId } from "@/lib/visitorId";

/**
 * First-party page-view tracking. Sends one fire-and-forget beacon per SPA route
 * change to POST /api/microsite/track. Never throws — analytics must not affect
 * the user experience.
 */

/** True when we should suppress tracking (prerender/headless/SSR contexts). */
function isAutomatedContext(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return true;
  // Puppeteer/Playwright/Selenium set navigator.webdriver — this is how the SEO
  // prerender (see microsite/scripts/prerender.mjs) is excluded from counts.
  if (navigator.webdriver) return true;
  // The prerendered snapshot still carries this guard until React clears it.
  if (document.documentElement.hasAttribute("data-prerender-mismatch")) return true;
  return false;
}

/** External referrer only — same-origin (internal SPA) referrers become "". */
function externalReferrer(): string {
  try {
    const ref = document.referrer;
    if (!ref) return "";
    return new URL(ref).host !== location.host ? ref : "";
  } catch {
    return "";
  }
}

export function trackPageview(path: string): void {
  if (!visitorTrackingEnabled || isAutomatedContext()) return;

  const payload = {
    path: path.split(/[?#]/)[0].slice(0, 512),
    visitorId: getVisitorId(),
    sessionId: getSessionId(),
    referrer: externalReferrer()
  };

  // Fire-and-forget: don't await, swallow every error.
  void publicApi.post("/microsite/track", payload).catch(() => {
    /* best-effort — offline, blocked, or backend down is fine */
  });
}
